/**
 * mega-menu-search.js
 * Predictive Search — Shopify Search API
 * Soporta múltiples instancias de input en la misma página
 * (barra inline desktop + panel mobile)
 */

(function () {
  'use strict';

  /* ── Guard standalone ──────────────────────────────────────────────────── */
  const scriptTag = document.querySelector('script[data-predictive-enabled]');
  if (scriptTag && scriptTag.dataset.predictiveEnabled === 'false') return;

  /* ── Configuración ─────────────────────────────────────────────────────── */
  const CONFIG = {
    debounceMs:   300,
    minChars:     2,
    resultsLimit: 5,
    endpoint:     '/search/suggest',
  };

  /* ════════════════════════════════════════════════════════════════════════
     INICIALIZAR TODAS LAS INSTANCIAS DE BÚSQUEDA
     Cada [data-search-input] + su [data-search-results] más cercano
     funcionan de forma independiente.
     ════════════════════════════════════════════════════════════════════════ */

  const cache = new Map(); // Cache compartido entre instancias

  document.querySelectorAll('[data-search-input]').forEach(input => {
    // Buscar el contenedor de resultados asociado a este input
    // Primero busca dentro del mismo contenedor padre, luego por aria-controls
    const controlsId = input.getAttribute('aria-controls');
    const resultsBox  = controlsId
      ? document.getElementById(controlsId)
      : input.closest('[role="search"]')?.querySelector('[data-search-results]');

    if (!resultsBox) return;

    // Botón limpiar dentro del mismo contenedor
    const container = input.closest('[role="search"]') || input.parentElement;
    const clearBtn  = container?.querySelector('[aria-label="Limpiar búsqueda"]');

    initSearchInstance(input, resultsBox, clearBtn);
  });

  /* ════════════════════════════════════════════════════════════════════════
     INSTANCIA DE BÚSQUEDA
     ════════════════════════════════════════════════════════════════════════ */

  function initSearchInstance(input, resultsBox, clearBtn) {
    const limit = parseInt(input.dataset.resultsLimit || CONFIG.resultsLimit, 10);
    let debounceTimer = null;
    let currentQuery  = '';
    let activeIndex   = -1;

    /* ── Eventos ─────────────────────────────────────────────────────────── */
    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeydown);

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        input.value = '';
        currentQuery = '';
        clearResults();
        toggleClearBtn(false);
        input.focus();
      });
    }

    // Cerrar al hacer clic fuera
    document.addEventListener('click', (e) => {
      if (!input.contains(e.target) && !resultsBox.contains(e.target)) {
        clearResults();
      }
    });

    /* ── Handlers ────────────────────────────────────────────────────────── */
    function onInput() {
      const query = input.value.trim();
      currentQuery = query;
      activeIndex  = -1;
      toggleClearBtn(query.length > 0);

      clearTimeout(debounceTimer);

      if (query.length < CONFIG.minChars) {
        clearResults();
        return;
      }

      debounceTimer = setTimeout(() => search(query), CONFIG.debounceMs);
    }

    function onKeydown(e) {
      const items = [...resultsBox.querySelectorAll('.mm-search-result-item')];
      if (!items.length && e.key !== 'Escape') return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        setActive(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, -1);
        if (activeIndex === -1) {
          items.forEach(i => i.classList.remove('is-active'));
          input.removeAttribute('aria-activedescendant');
        } else {
          setActive(items);
        }
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        const link = items[activeIndex]?.querySelector('a') || items[activeIndex];
        if (link?.href) window.location = link.href;
      } else if (e.key === 'Escape') {
        clearResults();
        input.value = '';
        toggleClearBtn(false);
      }
    }

    function setActive(items) {
      items.forEach((item, i) => {
        const active = i === activeIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active);
        if (active) {
          item.scrollIntoView({ block: 'nearest' });
          input.setAttribute('aria-activedescendant', item.id || '');
        }
      });
    }

    /* ── Fetch + Cache ───────────────────────────────────────────────────── */
    async function search(query) {
      const key = query.toLowerCase().trim();

      if (cache.has(key)) {
        renderResults(cache.get(key), query);
        return;
      }

      renderSkeleton();

      try {
        // URLSearchParams codifica [] como %5B%5D — Shopify no lo acepta.
        // Construimos el query string manualmente para preservar los corchetes.
        const queryString = [
          `q=${encodeURIComponent(query)}`,
          `resources[type]=product`,
          `resources[limit]=${limit}`,
          `resources[options][unavailable_products]=last`,
          `resources[options][fields]=title,product_type,variants.sku,vendor,tag`,
        ].join('&');

        const res = await fetch(`${CONFIG.endpoint}.json?${queryString}`, {
          headers: { 'Accept': 'application/json' },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        console.log('[MegaMenu Search] API response:', data);  // debug — quitar en producción
        const products = parseProducts(data);

        if (query === currentQuery) {
          cache.set(key, products);
          renderResults(products, query);
        }

      } catch (err) {
        console.warn('[MegaMenu Search]', err);
        renderError();
      }
    }

    /* ── Parse ───────────────────────────────────────────────────────────── */
    function parseProducts(data) {
      return (data?.resources?.results?.products || []).map(p => ({
        title:    p.title,
        url:      p.url,
        image:    p.featured_image?.url || null,
        imageAlt: p.featured_image?.alt || p.title,
        price:    p.price,
        sku:      p.variants?.[0]?.sku || null,
        type:     p.product_type || null,
      }));
    }

    /* ── Render ──────────────────────────────────────────────────────────── */
    function renderResults(products, query) {
      if (!products.length) { renderEmpty(query); return; }

      input.setAttribute('aria-expanded', 'true');
      activeIndex = -1;

      const items = products.map((p, i) => {
        const imgHTML = p.image
          ? `<img class="mm-search-result-item__image" src="${esc(p.image)}&width=120" alt="${esc(p.imageAlt)}" width="56" height="56" loading="lazy">`
          : `<div class="mm-search-result-item__image mm-search-result-item__image--placeholder">${iconImg()}</div>`;

        const skuHTML  = p.sku  ? `<span class="mm-search-result-item__sku">SKU: ${esc(p.sku)}</span>` : '';
        const typeHTML = p.type ? `<span class="mm-search-result-item__category">${esc(p.type)}</span>` : '';
        const price    = p.price ? `<span class="mm-search-result-item__price">${esc(p.price)}</span>` : '';

        return `<a href="${esc(p.url)}" class="mm-search-result-item" id="mm-sr-${Date.now()}-${i}" role="option" aria-selected="false">
          ${imgHTML}
          <div class="mm-search-result-item__info">
            <p class="mm-search-result-item__title">${highlight(esc(p.title), query)}</p>
            <div class="mm-search-result-item__meta">${skuHTML}${typeHTML}</div>
          </div>
          ${price}
        </a>`;
      }).join('');

      const footer = `<div class="mm-search-results__footer">
        <a href="/search?q=${encodeURIComponent(query)}&type=product" class="mm-search-results__view-all">
          Ver todos los resultados para "<strong>${esc(query)}</strong>"
        </a>
      </div>`;

      resultsBox.innerHTML = items + footer;
    }

    function renderSkeleton() {
      input.setAttribute('aria-expanded', 'true');
      const row = `<div class="mm-search-result-item" aria-hidden="true">
        <div class="mm-skeleton" style="width:56px;height:56px;border-radius:8px;flex-shrink:0"></div>
        <div class="mm-search-result-item__info">
          <div class="mm-skeleton" style="width:65%;height:13px;margin-bottom:8px"></div>
          <div class="mm-skeleton" style="width:40%;height:11px"></div>
        </div>
        <div class="mm-skeleton" style="width:44px;height:13px"></div>
      </div>`;
      resultsBox.innerHTML = row.repeat(3);
    }

    function renderEmpty(query) {
      input.setAttribute('aria-expanded', 'false');
      resultsBox.innerHTML = `<p class="mm-search-results__empty">Sin resultados para "<strong>${esc(query)}</strong>"</p>`;
    }

    function renderError() {
      resultsBox.innerHTML = `<p class="mm-search-results__empty">Error al buscar. Intenta de nuevo.</p>`;
    }

    function clearResults() {
      resultsBox.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function toggleClearBtn(show) {
      if (clearBtn) clearBtn.hidden = !show;
    }
  }

  /* ── Utilidades globales ───────────────────────────────────────────────── */
  function highlight(text, query) {
    if (!query) return text;
    const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(re, '<mark class="mm-search-highlight">$1</mark>');
  }

  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function iconImg() {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <path stroke-linecap="round" d="M21 15l-5-5L5 21"/>
    </svg>`;
  }

})();
